import { RefObject, useEffect, useState } from 'react';

export type BackgroundTone = 'dark' | 'light';

type RgbaColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

const DEFAULT_BACKGROUND: RgbaColor = {
  r: 255,
  g: 255,
  b: 255,
  a: 1,
};

/**
 * Ab dieser relativen Helligkeit bietet ein dunkles Logo
 * mehr Kontrast als ein weisses Logo.
 */
const LUMINANCE_THRESHOLD = 0.179;

function parseCssColor(value: string): RgbaColor | null {
  if (!value || value === 'transparent') {
    return null;
  }

  const match = value.match(
      /^rgba?\(\s*([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:\s*(?:,|\/)\s*([\d.]+))?\s*\)$/i,
  );

  if (!match) {
    return null;
  }

  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
    a: match[4] !== undefined ? Number(match[4]) : 1,
  };
}

/**
 * Legt eine halbtransparente Vordergrundfarbe
 * über eine Hintergrundfarbe.
 */
function compositeColors(
    foreground: RgbaColor,
    background: RgbaColor,
): RgbaColor {
  const alpha =
      foreground.a + background.a * (1 - foreground.a);

  if (alpha === 0) {
    return {
      r: 0,
      g: 0,
      b: 0,
      a: 0,
    };
  }

  return {
    r:
        (foreground.r * foreground.a +
            background.r * background.a * (1 - foreground.a)) /
        alpha,
    g:
        (foreground.g * foreground.a +
            background.g * background.a * (1 - foreground.a)) /
        alpha,
    b:
        (foreground.b * foreground.a +
            background.b * background.a * (1 - foreground.a)) /
        alpha,
    a: alpha,
  };
}

function toLinearColorChannel(channel: number): number {
  const normalized = channel / 255;

  return normalized <= 0.04045
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

function getRelativeLuminance(color: RgbaColor): number {
  const r = toLinearColorChannel(color.r);
  const g = toLinearColorChannel(color.g);
  const b = toLinearColorChannel(color.b);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getToneFromColor(color: RgbaColor): BackgroundTone {
  const luminance = getRelativeLuminance(color);

  return luminance > LUMINANCE_THRESHOLD ? 'light' : 'dark';
}

function isLogoElement(
    element: Element,
    logoElement: HTMLElement,
): boolean {
  return element === logoElement || logoElement.contains(element);
}

function getBaseBackgroundColor(): RgbaColor {
  let result = DEFAULT_BACKGROUND;

  const baseElements = [
    document.documentElement,
    document.body,
  ];

  for (const element of baseElements) {
    const computedStyle = window.getComputedStyle(element);
    const color = parseCssColor(computedStyle.backgroundColor);

    if (color && color.a > 0) {
      result = compositeColors(color, result);
    }
  }

  return result;
}

function getBackgroundColorAtPoint(
    x: number,
    y: number,
    logoElement: HTMLElement,
): RgbaColor {
  const elements = document.elementsFromPoint(x, y);
  const candidates: Array<{
    color: RgbaColor;
    score: number;
  }> = [];

  for (const element of elements) {
    if (
        element === document.body ||
        element === document.documentElement ||
        isLogoElement(element, logoElement)
    ) {
      continue;
    }

    let current: Element | null = element;

    while (current) {
      const currentStyle = window.getComputedStyle(current);
      const color = parseCssColor(currentStyle.backgroundColor);

      if (color && color.a > 0) {
        const rect = current.getBoundingClientRect();
        const widthRatio = rect.width / window.innerWidth;
        const heightRatio = rect.height / window.innerHeight;
        const area = Math.max(rect.width, 0) * Math.max(rect.height, 0);

        let score = area;

        if (current === document.body || current === document.documentElement) {
          score += 1_000_000_000;
        }

        if (widthRatio >= 0.85) {
          score += 100_000_000;
        }

        if (heightRatio >= 0.35) {
          score += 10_000_000;
        }

        candidates.push({ color, score });
      }

      current = current.parentElement;
    }
  }

  if (candidates.length === 0) {
    return getBaseBackgroundColor();
  }

  candidates.sort((a, b) => b.score - a.score);

  return candidates[0].color;
}

/**
 * Erkennt, ob sich das Logo über einem hellen oder dunklen
 * Hintergrund befindet.
 *
 * Rückgabewert:
 * - "light": Hintergrund ist hell → dunkles Logo verwenden
 * - "dark": Hintergrund ist dunkel → weisses Logo verwenden
 */
export function useBackgroundColor(
    elementRef: RefObject<HTMLElement | null>,
): BackgroundTone {
  const [backgroundTone, setBackgroundTone] =
      useState<BackgroundTone>('light');

  useEffect(() => {
    let animationFrameId: number | null = null;

    const detectBackgroundColor = () => {
      const logoElement = elementRef.current;

      if (!logoElement) {
        return;
      }

      const rect = logoElement.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      const samplePoints = [
        {
          x: Math.max(8, window.innerWidth * 0.08),
          y: rect.bottom + 12,
        },
        {
          x: window.innerWidth * 0.25,
          y: rect.bottom + 12,
        },
        {
          x: window.innerWidth * 0.5,
          y: rect.bottom + 12,
        },
        {
          x: window.innerWidth * 0.75,
          y: rect.bottom + 12,
        },
        {
          x: Math.min(window.innerWidth - 8, window.innerWidth * 0.92),
          y: rect.bottom + 12,
        },
        {
          x: window.innerWidth * 0.5,
          y: rect.bottom + 28,
        },
      ];

      const detectedTones: BackgroundTone[] = [];

      for (const point of samplePoints) {
        const backgroundColor = getBackgroundColorAtPoint(
            point.x,
            point.y,
            logoElement,
        );

        detectedTones.push(getToneFromColor(backgroundColor));
      }

      const darkCount = detectedTones.filter((tone) => tone === 'dark').length;
      const lightCount = detectedTones.length - darkCount;

      setBackgroundTone((currentTone) => {
        if (darkCount === lightCount) {
          return currentTone;
        }

        return darkCount > lightCount ? 'dark' : 'light';
      });
    };

    const scheduleDetection = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        detectBackgroundColor();
      });
    };

    scheduleDetection();

    /*
     * Capture=true erkennt auch Scroll-Events in inneren
     * Scroll-Containern.
     */
    document.addEventListener('scroll', scheduleDetection, {
      capture: true,
      passive: true,
    });

    window.addEventListener('resize', scheduleDetection, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(scheduleDetection);

    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      document.removeEventListener(
          'scroll',
          scheduleDetection,
          true,
      );

      window.removeEventListener('resize', scheduleDetection);

      resizeObserver.disconnect();

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [elementRef]);

  return backgroundTone;
}
